import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Link2, Scissors, Trash2, AlertTriangle, Camera, Send } from "lucide-react";

export function ClosureEditor({ closureId, closureNumber, closureLat, closureLng, flow = [], onChanged }) {
  const [cables, setCables] = useState([]);
  const [pt, setPt] = useState([]);
  const [busy, setBusy] = useState(false);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [fa, setFa] = useState(1);
  const [fb, setFb] = useState(1);
  const [repOpen, setRepOpen] = useState(false);
  const [repDesc, setRepDesc] = useState("");
  const [repPhoto, setRepPhoto] = useState(null);
  const [repBusy, setRepBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.get(`/closures/${closureId}/cables`),
        api.get(`/closures/${closureId}/pass-through`),
      ]);
      setCables([...(c.data.cables || []), ...(c.data.nearby || [])]);
      setPt(p.data.pass_through || []);
    } catch { /* ignore */ }
  }, [closureId]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => { await load(); onChanged && onChanged(); };
  const cName = (id) => (cables.find((x) => x.id === id) || {}).number || "?";
  const selCls = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm";

  const autoSplice = async () => {
    if (!a || !b || a === b) return toast.error("Wybierz dwa różne kable");
    setBusy(true);
    try {
      const { data } = await api.post(`/closures/${closureId}/splice-auto`, { cable_a_id: a, cable_b_id: b });
      toast.success(`Zespawano 1:1: ${data.created} włókien`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); } finally { setBusy(false); }
  };
  const manualSplice = async () => {
    if (!a || !b) return toast.error("Wybierz kable A i B");
    setBusy(true);
    try {
      await api.post(`/closures/${closureId}/splice-manual`, { cable_in_id: a, fiber_in: Number(fa), cable_out_id: b, fiber_out: Number(fb) });
      toast.success(`Spaw ${cName(a)}/${fa} ↔ ${cName(b)}/${fb}`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); } finally { setBusy(false); }
  };
  const delSplice = async (sid) => {
    if (!sid) return;
    setBusy(true);
    try { await api.delete(`/closures/splice/${sid}`); toast.success("Usunięto spaw"); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Błąd"); } finally { setBusy(false); }
  };
  const toggleCut = async (cableId, fiber, isCut) => {
    setBusy(true);
    try {
      if (isCut) {
        await api.post(`/closures/${closureId}/uncut-fiber`, { cable_id: cableId, fiber });
      } else {
        let r = await api.post(`/closures/${closureId}/cut-fiber`, { cable_id: cableId, fiber });
        if (r.data && r.data.ok === false && r.data.occupied) {
          if (!window.confirm(r.data.detail || "Włókno zajęte — przeciąć mimo to?")) { setBusy(false); return; }
          await api.post(`/closures/${closureId}/cut-fiber`, { cable_id: cableId, fiber, confirm: true });
        }
      }
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); } finally { setBusy(false); }
  };

  const submitReport = async () => {
    setRepBusy(true);
    try {
      let lat = closureLat, lng = closureLng;
      if ((lat == null || lng == null) && navigator.geolocation) {
        try {
          const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 6000 }));
          lat = pos.coords.latitude; lng = pos.coords.longitude;
        } catch { /* brak GPS */ }
      }
      const { data: ticket } = await api.post("/tickets", {
        title: `Usterka mufy ${closureNumber || String(closureId).slice(0, 8)}`,
        description: repDesc || "Zgłoszenie usterki mufy z aplikacji montera",
        priority: "wysoki", category: "Mufa",
        link_module: "closures", link_id: closureId, link_label: closureNumber || "",
        lat, lng,
      });
      if (repPhoto && ticket?.id) {
        const fd = new FormData();
        fd.append("entity_type", "fault"); fd.append("entity_id", ticket.id); fd.append("file", repPhoto);
        try { await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch { /* zdjęcie opcjonalne */ }
      }
      toast.success(`Zgłoszono usterkę ${ticket?.ticket_no || ""}`.trim());
      setRepOpen(false); setRepDesc(""); setRepPhoto(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Nie udało się zgłosić usterki"); }
    finally { setRepBusy(false); }
  };

  return (
    <div className="mt-3 space-y-3 border-t border-amber-700/40 pt-3" data-testid="closure-editor">
      {busy && <div className="flex items-center gap-2 text-amber-300 text-xs"><Loader2 size={14} className="animate-spin" /> Przetwarzanie…</div>}

      <div className="grid grid-cols-2 gap-2">
        <select value={a} onChange={(e) => setA(e.target.value)} data-testid="ce-cable-a" className={selCls}>
          <option value="">Kabel A…</option>
          {cables.map((c) => <option key={c.id} value={c.id}>{c.number} ({c.free}/{c.fiber_count} wolnych)</option>)}
        </select>
        <select value={b} onChange={(e) => setB(e.target.value)} data-testid="ce-cable-b" className={selCls}>
          <option value="">Kabel B…</option>
          {cables.map((c) => <option key={c.id} value={c.id}>{c.number} ({c.free}/{c.fiber_count} wolnych)</option>)}
        </select>
      </div>

      <button onClick={autoSplice} disabled={busy} data-testid="ce-splice-auto"
        className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 rounded-lg px-3 py-2.5 text-sm font-semibold flex items-center justify-center gap-2">
        <Link2 size={15} /> Zespawaj 1:1 (wszystkie wolne włókna)
      </button>

      <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 space-y-2" data-testid="ce-manual">
        <div className="text-xs text-cyan-300 font-semibold">Spaw ręczny (włókno ↔ włókno)</div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" min="1" value={fa} onChange={(e) => setFa(e.target.value)} data-testid="ce-fiber-a" placeholder="Włókno A" className={selCls} />
          <input type="number" min="1" value={fb} onChange={(e) => setFb(e.target.value)} data-testid="ce-fiber-b" placeholder="Włókno B" className={selCls} />
        </div>
        <button onClick={manualSplice} disabled={busy} data-testid="ce-splice-manual"
          className="w-full bg-slate-700 hover:bg-slate-600 disabled:opacity-60 rounded px-3 py-2 text-sm flex items-center justify-center gap-2">
          <Link2 size={14} /> Zespawaj A/{fa} ↔ B/{fb}
        </button>
      </div>

      <div data-testid="ce-splices">
        <div className="text-xs text-cyan-300 font-semibold mb-1">Spawy w mufie ({flow.length})</div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {flow.length === 0 && <div className="text-xs text-slate-500">Brak spawów.</div>}
          {flow.map((f, i) => (
            <div key={f.id || i} className="flex items-center gap-2 bg-slate-900/50 rounded px-2 py-1 text-xs font-mono" data-testid={`ce-splice-${f.id || i}`}>
              <span className="text-slate-300">{f.cable_number || "?"}</span>
              <span className="text-cyan-300">wł.{f.fiber_in ?? "?"}</span>
              <span className="text-slate-500">→</span>
              <span className="text-green-300">{f.target_type === "Splitter" ? "SPLITER" : `${f.cable_out_number || "?"} wł.${f.fiber_out ?? "?"}`}</span>
              {f.id && <button onClick={() => delSplice(f.id)} disabled={busy} data-testid={`ce-splice-del-${f.id}`} className="ml-auto text-red-300 hover:text-red-200"><Trash2 size={13} /></button>}
            </div>
          ))}
        </div>
      </div>

      {pt.length > 0 && (
        <div data-testid="ce-passthrough">
          <div className="text-xs text-amber-300 font-semibold mb-1 flex items-center gap-1"><Scissors size={13} /> Kable przelotowe — kliknij włókno, aby przeciąć/przywrócić</div>
          {pt.map((c) => {
            const cut = new Set((c.cut_fibers || []).map(Number));
            return (
              <div key={c.cable_id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-2 mb-2" data-testid={`ce-pt-${c.cable_id}`}>
                <div className="text-xs text-slate-300 mb-1">{c.cable_number} · {c.fiber_count} wł.</div>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: c.fiber_count }, (_, k) => k + 1).map((n) => {
                    const isCut = cut.has(n);
                    const f = (c.fibers || []).find((x) => x.number === n);
                    const occ = f && (f.status === "zajęte" || f.status === "zajete");
                    return (
                      <button key={n} onClick={() => toggleCut(c.cable_id, n, isCut)} disabled={busy}
                        data-testid={`ce-cut-${c.cable_id}-${n}`}
                        title={isCut ? "Przecięte — kliknij, aby przywrócić" : (occ ? "Zajęte" : "Wolne")}
                        className={`w-7 h-7 rounded text-[10px] font-mono border ${isCut ? "bg-red-600 border-red-400 text-white line-through" : occ ? "bg-amber-500/20 border-amber-500 text-amber-200" : "bg-green-500/15 border-green-600 text-green-200"}`}>
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="border-t border-red-800/40 pt-3" data-testid="closure-report">
        {!repOpen ? (
          <button onClick={() => setRepOpen(true)} data-testid="closure-report-toggle"
            className="w-full bg-red-700 hover:bg-red-600 rounded-lg px-3 py-2.5 text-sm font-semibold flex items-center justify-center gap-2">
            <AlertTriangle size={15} /> Zgłoś usterkę mufy
          </button>
        ) : (
          <div className="space-y-2" data-testid="closure-report-form">
            <div className="text-xs text-red-300 font-semibold flex items-center gap-1"><AlertTriangle size={13} /> Zgłoszenie usterki mufy</div>
            <textarea value={repDesc} onChange={(e) => setRepDesc(e.target.value)} rows={2} placeholder="Opis usterki (co się dzieje?)"
              data-testid="closure-report-desc" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm" />
            <label className="flex items-center gap-2 bg-slate-900 border border-dashed border-slate-600 rounded px-3 py-2.5 cursor-pointer hover:border-red-500 text-sm text-slate-300">
              <Camera size={16} className="text-red-400 shrink-0" />
              <span className="truncate">{repPhoto ? repPhoto.name : "Dodaj zdjęcie (aparat)"}</span>
              <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="closure-report-photo"
                onChange={(e) => setRepPhoto(e.target.files?.[0] || null)} />
            </label>
            <div className="text-[11px] text-slate-500">Lokalizacja: {(closureLat != null && closureLng != null) ? `${Number(closureLat).toFixed(5)}, ${Number(closureLng).toFixed(5)} (mufa)` : "zostanie pobrana z GPS telefonu"}</div>
            <div className="flex gap-2">
              <button onClick={() => { setRepOpen(false); setRepPhoto(null); }} className="flex-1 bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm">Anuluj</button>
              <button onClick={submitReport} disabled={repBusy} data-testid="closure-report-submit"
                className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-60 rounded px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2">
                {repBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Wyślij zgłoszenie
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
